import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Wrench,
  Calendar,
  Users,
  UserPlus,
  HardHat,
  BarChart3,
  Bell,
  LogOut,
  Menu,
  X,
  Package,
  ClipboardList,
  ShoppingCart,
  Clock,
  Coins,
  Target,
  Construction,
  Shield,
  ShieldAlert,
  UserCog,
  ClipboardCheck,
  Settings,
  AlertTriangle,
  NotebookPen,
  Briefcase,
  Banknote,
  Wallet,
  Cog,
  ChevronDown,
  ChevronRight,
  List,
  FileText,
  Receipt,
  Archive,
} from 'lucide-react';

const Layout = () => {
  const { user, logout, canAccessMenu, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Grouped, collapsible menu structure (per MBConstruct menu spec)
  const menu = [
    { kind: 'link', name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard', menuKey: 'dashboard' },
    { kind: 'group', key: 'operations', name: 'Operations', icon: Briefcase, children: [
      { name: 'Leads', path: '/leads', icon: Target, menuKey: 'leads' },
      { name: 'Jobs', path: '/jobs', icon: Wrench, menuKey: 'jobs' },
      { name: 'Fault Log', path: '/fault-log', icon: AlertTriangle, menuKey: 'fault_log' },
      { name: 'Site Diary', path: '/site-diary', icon: NotebookPen, menuKey: 'site_diary' },
      { name: 'Calendar', path: '/schedule', icon: Calendar, menuKey: 'calendar' },
      { name: 'BOQ', path: '/boq', icon: ClipboardList, menuKey: 'boq' },
      { name: 'Requisitions', path: '/requisitions', icon: ShoppingCart, menuKey: 'requisitions' },
      { name: 'Time & Attendance', path: '/time-attendance', icon: Clock, menuKey: 'time_attendance' },
      { name: 'Incidents', path: '/incident-reporting', icon: ShieldAlert, menuKey: 'incidents' },
      { name: 'Asset Register', path: '/assets', icon: Package, menuKey: 'assets' },
      { name: 'Reports', path: '/reports', icon: BarChart3, menuKey: 'reports' },
      { name: 'Performance Reviews', path: '/performance-reviews', icon: ClipboardCheck, menuKey: 'performance_reviews' },
    ] },
    { kind: 'group', key: 'finance', name: 'Finance', icon: Banknote, children: [
      { name: 'Cashflow', path: '/cashflow', icon: Coins, menuKey: 'cashflow' },
      { name: 'Purchase Orders', path: '/finance/purchase-orders', icon: ShoppingCart, menuKey: 'purchase_orders' },
      { name: 'Invoicing', path: '/finance/invoicing', icon: FileText, menuKey: 'invoicing' },
      { name: 'Payment Certificates', path: '/finance/payment-certificates', icon: Receipt, menuKey: 'payment_certificates' },
      { name: 'Claims', path: '/finance/claims', icon: FileText, menuKey: 'claims' },
      { name: 'Payroll', path: '/finance/payroll', icon: Wallet, menuKey: 'payroll' },
    ] },
    { kind: 'group', key: 'sysadmin', name: 'Sys Admin', icon: Cog, children: [
      { name: 'Team Members', path: '/team-members', icon: UserPlus, menuKey: 'team_members' },
      { name: 'Teams', path: '/teams', icon: Users, menuKey: 'teams' },
      { name: 'Lookup Maintenance', path: '/lookup', icon: List, menuKey: 'lookup' },
      { name: 'User Management', path: '/user-management', icon: UserCog, menuKey: 'user_management' },
      { name: 'Archived Jobs', path: '/archived-jobs', icon: Archive, menuKey: 'user_management', superAdminOnly: true },
      { name: 'POPIA Compliance', path: '/popia-compliance', icon: Shield, menuKey: 'popia' },
      { name: 'System Settings', path: '/system-settings', icon: Settings, menuKey: 'system_settings' },
    ] },
  ];

  const canSeeLeaf = (item) => {
    if (item.superAdminOnly && !isSuperAdmin()) return false;
    return !item.menuKey || canAccessMenu(item.menuKey);
  };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const slugify = (name) => 'nav-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  // Auto-expand the group/subgroup that contains the active route
  useEffect(() => {
    const next = {};
    menu.forEach((g) => {
      if (g.kind !== 'group') return;
      const groupHit = g.children.some((c) =>
        c.kind === 'subgroup' ? c.children.some((s) => isActive(s.path)) : isActive(c.path));
      if (groupHit) next[g.key] = true;
      g.children.forEach((c) => {
        if (c.kind === 'subgroup' && c.children.some((s) => isActive(s.path))) next['sub_' + c.key] = true;
      });
    });
    if (Object.keys(next).length) setExpanded((prev) => ({ ...prev, ...next }));
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-slate-50">
      <aside
        className={`sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 h-screen bg-slate-900 text-white transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          <div className="p-3 border-b border-slate-800">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <img
                  src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png"
                  alt="MBConstruction"
                  className="w-11 h-11 rounded-lg object-contain bg-white p-1 shadow"
                />
                <p
                  className="text-[10px] font-semibold tracking-[0.25em] text-orange-500 leading-tight"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  data-testid="sidebar-wordmark"
                >
                  MBC<br/>OPERATIONS
                </p>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
            {menu.map((node) => {
              if (node.kind === 'link') {
                if (!canSeeLeaf(node)) return null;
                const active = isActive(node.path);
                return (
                  <Link
                    key={node.path}
                    to={node.path}
                    data-testid={node.testId}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <node.icon className="w-5 h-5" />
                    <span className="font-medium">{node.name}</span>
                  </Link>
                );
              }

              // group
              const visibleChildren = node.children.filter((c) =>
                c.kind === 'subgroup' ? (canSeeLeaf(c) && c.children.some(canSeeLeaf)) : canSeeLeaf(c));
              if (visibleChildren.length === 0) return null;
              const open = !!expanded[node.key];
              return (
                <div key={node.key}>
                  <button
                    type="button"
                    data-testid={`navgrp-${node.key}`}
                    onClick={() => toggle(node.key)}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-slate-200 hover:bg-slate-800 transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <node.icon className="w-5 h-5" />
                      <span className="font-semibold text-xs uppercase tracking-wider">{node.name}</span>
                    </span>
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  {open && (
                    <div className="mt-1 ml-4 pl-2 border-l border-slate-800 space-y-1">
                      {visibleChildren.map((c) => {
                        if (c.kind === 'subgroup') {
                          const subKey = 'sub_' + c.key;
                          const subOpen = !!expanded[subKey];
                          const subChildren = c.children.filter(canSeeLeaf);
                          return (
                            <div key={c.key}>
                              <button
                                type="button"
                                data-testid={`navsub-${c.key}`}
                                onClick={() => toggle(subKey)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 transition-colors"
                              >
                                <span className="flex items-center gap-2">
                                  <c.icon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{c.name}</span>
                                </span>
                                {subOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                              {subOpen && (
                                <div className="mt-1 ml-3 pl-2 border-l border-slate-800 space-y-0.5">
                                  {subChildren.map((s) => (
                                    <Link
                                      key={s.path}
                                      to={s.path}
                                      data-testid={slugify(s.name)}
                                      onClick={() => setSidebarOpen(false)}
                                      className={`block px-3 py-1.5 rounded-md text-sm transition-colors ${
                                        isActive(s.path) ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                      }`}
                                    >
                                      {s.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        const active = isActive(c.path);
                        return (
                          <Link
                            key={c.path}
                            to={c.path}
                            data-testid={slugify(c.name)}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            <c.icon className="w-4 h-4" />
                            <span className="font-medium">{c.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-800">
            <Link to="/profile">
              <div className="flex items-center gap-3 mb-4 px-2 hover:bg-slate-800 rounded-lg py-2 transition-colors cursor-pointer">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-slate-400">{user?.role}</p>
                </div>
              </div>
            </Link>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between lg:justify-end">
          <button
            data-testid="mobile-menu-open"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600 hover:text-slate-900"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            <Link to="/notifications" data-testid="notifications-link">
              <button className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <Bell className="w-5 h-5" />
              </button>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;